import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import {
  Radio as BaseRadio,
  Checkbox as BaseCheckbox,
  Box,
  ButtonText,
  Flex,
} from 'Components/Base';
import BaseHTMLView from 'Components/HTMLView';
import { EXAM_PAPER_ITEM_TYPE } from 'Components/Quiz/constant';
import { theme } from 'Components/Theme';

const HTMLView = styled((props) => <BaseHTMLView {...props} />)`
  font-size: 18px;
  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ul,
  ol {
    margin: 0;
    padding: 0;
  }
`;

const BaseChoiceWrapper = styled(ButtonText)`
  ${({ theme, variant }) => css`
    display: flex;
    align-items: center;
    padding: ${theme.space[6]}px;
    border-radius: 16px;
    color: ${theme.colors.text};
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.white};
    text-align: left;
    cursor: ${variant === 'default' ? 'pointer' : 'default'};

    &:focus,
    &:hover {
      text-decoration: none;
      color: ${theme.colors.text};
      background-color: ${theme.colors.white};
    }

    @media (max-width: ${theme.breakpoints[1]}) {
      padding: ${theme.space[4]}px;
    }
  `}
`;

const SolutionChoiceWrapper = styled(BaseChoiceWrapper)`
  ${({ theme }) => css`
    &.correct {
      border: 1px solid ${theme.colors.backgroundGreen};
      background-color: ${theme.colors.backgroundGreenLight};
      color: ${theme.colors.textWhite};
    }

    &.incorrect {
      border: 1px solid ${theme.colors.backgroundYellow};
      background-color: ${theme.colors.backgroundYellowLight};
      color: ${theme.colors.textWhite};
    }
  `}
`;

const ChoiceWrapper = styled(BaseChoiceWrapper)`
  ${({ theme }) => css`
    &:focus {
      text-decoration: none;
      color: ${theme.colors.text};
      background-color: ${theme.colors.guidedPractice.thumbnailCardBackground};
    }

    &.selected {
      border-color: ${theme.colors.primary};
      border: 1px solid ${theme.colors.primary};
      background-color: ${theme.colors.guidedPractice.thumbnailCardBackground};
      color: ${theme.colors.textWhite};
    }
  `}
`;

export const CustomRadio = styled(Box)`
  ${({ theme }) => css`
    min-width: 24px;
    min-height: 24px;
    border-radius: 50%;
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.greyscale.grey4};

    &.correct {
      background-color: ${theme.colors.backgroundGreen};
      border: 1px solid ${theme.colors.backgroundGreen};
    }

    &.incorrect {
      background-color: ${theme.colors.backgroundYellow};
      border: 1px solid ${theme.colors.backgroundYellow};
    }
  `}
`;

export const CustomCheckbox = styled(Box)`
  ${({ theme }) => css`
    min-width: 24px;
    min-height: 24px;
    border-radius: 4px;
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.greyscale.grey4};

    &.correct {
      background-color: ${theme.colors.backgroundGreen};
      border: 1px solid ${theme.colors.backgroundGreen};
    }

    &.incorrect {
      background-color: ${theme.colors.backgroundYellow};
      border: 1px solid ${theme.colors.backgroundYellow};
    }
  `}
`;

const choiceCommonPropTypes = {
  choice: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  questionType: PropTypes.oneOf(Object.values(EXAM_PAPER_ITEM_TYPE)),
};

const ExamSolutionVariant = ({ choice, isCorrect, questionType }) => {
  return (
    <SolutionChoiceWrapper
      variant="solution"
      className={isCorrect === null ? '' : isCorrect ? 'correct' : 'incorrect'}
    >
      <Flex alignItems="center" gridColumnGap={4} overflow="hidden">
        {questionType === EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT ? (
          <CustomCheckbox
            className={isCorrect === null ? '' : isCorrect ? 'correct' : 'incorrect'}
          />
        ) : (
          <CustomRadio className={isCorrect === null ? '' : isCorrect ? 'correct' : 'incorrect'} />
        )}
        <HTMLView rawContent={choice.label} />
      </Flex>
    </SolutionChoiceWrapper>
  );
};

ExamSolutionVariant.propTypes = {
  ...choiceCommonPropTypes,
  isCorrect: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
};

const Checkbox = styled((props) => <BaseCheckbox color={theme.colors.primary} {...props} />)`
  padding: 0 !important;
`;

const Radio = styled((props) => <BaseRadio color={theme.colors.primary} {...props} />)`
  padding: 0 !important;
`;

const ExamDefaultVariant = ({ choice, isSelected, onClick, questionType }) => {
  return (
    <ChoiceWrapper variant="default" onClick={onClick} className={isSelected ? 'selected' : ''}>
      <Flex alignItems="center" gridColumnGap={4} overflow="hidden">
        {questionType === EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT ? (
          <Checkbox checked={isSelected} size={24} />
        ) : (
          <Radio checked={isSelected} size={24} />
        )}
        <HTMLView rawContent={choice.label} />
      </Flex>
    </ChoiceWrapper>
  );
};

ExamDefaultVariant.propTypes = {
  ...choiceCommonPropTypes,
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
};

const ExamChoice = ({
  choice,
  onClick,
  isSelected,
  isCorrect,
  index,
  variant = 'default',
  questionType,
}) => {
  const handleClick = () => onClick(index);

  if (variant === 'default') {
    return (
      <ExamDefaultVariant
        questionType={questionType}
        choice={choice}
        isSelected={isSelected}
        onClick={handleClick}
      />
    );
  }
  return <ExamSolutionVariant questionType={questionType} choice={choice} isCorrect={isCorrect} />;
};

ExamChoice.propTypes = {
  ...choiceCommonPropTypes,
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
  isCorrect: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
  index: PropTypes.number,
  variant: PropTypes.oneOf(['default', 'solution']),
};

export default ExamChoice;
